import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { CorteProveedorService } from '../services/corteProveedor.service';
import { GenericTableHandlerService } from '../shared/GenericTableHandlerService.service';
import { CortesDialogComponent } from './cortes-dialog/cortes-dialog.component';
import { Sort } from '@angular/material/sort';
import { AuthService } from '../services/auth.service';
import { take } from 'rxjs/operators';
import { TableAction } from '../table-generic/table-generic.component';

@Component({
  selector: 'app-cortes-lista',
  templateUrl: './cortes-lista.component.html',
  styleUrls: ['./cortes-lista.component.scss']
})
export class CortesListaComponent extends GenericTableHandlerService implements OnInit {
  @Output() detailsClick = new EventEmitter<any>();
  isAdministrador = false;

  constructor(
    protected corteService: CorteProveedorService,
    override dialog: MatDialog,
    override toastr: ToastrService,
    private authService: AuthService
  ) {
    super(corteService, dialog, toastr);
  }

  ngOnInit(): void {
    // Verificar primero si el usuario ya está disponible en el snapshot
    const currentUser = this.authService.getCurrentUserSnapshot();
    
    if (currentUser?.['rol']) {
      // Verificar si el rol es "Administrador"
      const rolNombre = typeof currentUser['rol'] === 'string' 
        ? currentUser['rol'] 
        : currentUser['rol']?.['rol'];
      
      this.isAdministrador = rolNombre === 'Administrador';
      this.initializeTable();
    } else {
      // Si no hay usuario en el snapshot, intentar obtenerlo del observable
      this.authService.currentUser$.pipe(take(1)).subscribe(user => {
        if (user?.['rol']) {
          const rolNombre = typeof user['rol'] === 'string' 
            ? user['rol'] 
            : user['rol']?.['rol'];
          
          this.isAdministrador = rolNombre === 'Administrador';
        }
        this.initializeTable();
      });
    }
  }

  private initializeTable(): void {
    // Construir acciones dinámicamente según el rol
    const acciones: TableAction[] = [
      { icon: 'visibility', tooltip: 'Ver detalles', emit: 'detailsClick' }
    ];

    // Agregar acción de eliminar solo si es administrador
    if (this.isAdministrador) {
      acciones.push({ 
        icon: 'delete', 
        tooltip: 'Eliminar corte', 
        color: 'warn',
        emit: 'deleteClick' 
      });
    }

    this.initialize(
      {
        title: 'Cortes por Proveedor',
        selectable: false,
        stickyHeader: true,
        pageSize: 10,
        pageSizeOptions: [5, 10, 25, 100],
        noDataMessage: 'No hay cortes registrados',
        columns: [
          { 
            key: 'id', 
            title: 'ID', 
            sortable: true, 
            width: '80px',
            align: 'center'
          },
          {
            key: 'createdAt',
            title: 'Fecha de Creación',
            type: 'date',
            sortable: true,
            format: 'dd/MM/yyyy HH:mm'
          },
          { 
            key: 'proveedor.nombre', 
            title: 'Proveedor', 
            sortable: true 
          },
          { 
            key: 'estado', 
            title: 'Estado', 
            sortable: true,
            type: 'badge',
            badge: {
              'pagado': 'success',
              'pendiente': 'warning',
              'cancelado': 'danger'
            }
          },
          {
            key: 'actions',
            title: 'Acciones',
            type: 'actions',
            actions: acciones
          }
        ]
      },
      null,
      ''
    );

    this.loadData();
  }

  override loadData(extraParams: Record<string, any> = {}): void {
    super.loadData(extraParams);
  }

  onViewDetails(corte: any): void {
    this.detailsClick.emit(corte);
    this.dialog.open(CortesDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      data: { corte: corte }
    });
  }

  override onSortChange(sort: Sort): void {
    this.currentSort$.next({
      key: sort.active,
      direction: (sort.direction || 'asc') as 'asc' | 'desc'
    });
    this.loadData();
  }

  onEdit(corte: any): void {
    // Opcional: Si necesitas editar cortes
    console.log('Editar corte', corte);
  }

  onDelete(corte: any): void {
    if (!corte || !corte.id) {
      this.toastr.warning('No se puede eliminar este corte');
      return;
    }

    // Confirmar eliminación
    const confirmMessage = `¿Está seguro de que desea eliminar el corte #${corte.id}?`;
    if (confirm(confirmMessage)) {
      this.loading$.next(true);
      // Usar el método delete del BaseService pasando el ID como path
      // El BaseService ya tiene el endpoint configurado
      this.baseService.delete(corte.id.toString()).subscribe({
        next: () => {
          this.toastr.success('Corte eliminado exitosamente');
          this.loadData();
          this.loading$.next(false);
        },
        error: (error) => {
          const errorMessage = error?.error?.message || error?.message || 'Error al eliminar el corte';
          this.toastr.error(errorMessage);
          this.loading$.next(false);
        }
      });
    }
  }
}

